import ApiService, { ApiResponse, ApiError, ApiErrorType } from '../../services/apiService';
import { API_CONFIG } from '../../config/appConfig';
import { ApiAdapter, BaseApiAdapter } from '../adapters/api-adapter';

/**
 * 基础服务类
 * 提供统一的API调用方式，处理Mock数据和真实API之间的切换
 */
export abstract class BaseService<T, R = any> {
  protected readonly useMockData: boolean;
  protected readonly baseUrl: string;
  protected readonly adapter: ApiAdapter<T, R>;

  /**
   * 构造函数
   * @param baseUrl API基础路径
   * @param adapter 数据适配器，用于转换API响应
   */
  constructor(baseUrl: string, adapter?: ApiAdapter<T, R>) {
    this.useMockData = API_CONFIG.USE_MOCK_DATA;
    this.baseUrl = baseUrl;
    this.adapter = adapter || new DefaultAdapter<T, R>();
  }

  /**
   * 获取API路径
   * @param path 相对路径
   * @returns 完整API路径
   */
  protected getApiPath(path: string): string {
    // 确保路径以斜杠开头
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${this.baseUrl}${normalizedPath}`;
  }

  /**
   * 处理API响应
   * @param response API响应
   * @returns 处理后的数据
   */
  protected handleResponse(response: ApiResponse<any>): T {
    // 如果响应包含data字段，使用data字段
    if (response.data && response.data.data) {
      return this.adapter.fromApiResponse({ data: response.data.data });
    }
    // 如果响应直接是数据，直接使用
    return this.adapter.fromApiResponse(response);
  }

  /**
   * 准备请求数据
   * @param data 原始数据
   * @returns 处理后的请求数据
   */
  protected prepareRequestData(data: T): R {
    return this.adapter.toApiRequest(data);
  }

  /**
   * 添加通用参数
   * @param params 原始参数
   * @returns 添加通用参数后的参数
   */
  protected addCommonParams(params: Record<string, any> = {}): Record<string, any> {
    // 如果参数中没有指定语言，则添加当前语言
    if (!params.lang) {
      // 优先使用i18next的当前语言设置
      let currentLang = 'en'; // 默认英文
      
      try {
        // 尝试从i18next获取当前语言
        const i18nextLang = localStorage.getItem('i18nextLng') || 'en';
        currentLang = i18nextLang.startsWith('zh') ? 'zh' : 'en';
      } catch (e) {
        // 如果获取失败，尝试从document.documentElement.lang获取
        const docLang = document.documentElement.lang || 'en';
        currentLang = docLang.startsWith('zh') || docLang === 'zh-CN' ? 'zh' : 'en';
      }
      
      params.lang = currentLang;
      console.log('🌐 [BaseService] 设置API语言参数:', currentLang);
    }
    
    return params;
  }

  /**
   * 获取Mock数据
   * 子类需要实现此方法以提供Mock数据
   * @param params 查询参数
   */
  protected abstract getMockData(params?: Record<string, any>): Promise<T>;

  /**
   * 获取数据
   * 根据环境变量决定使用Mock数据还是真实API
   * @param path API路径
   * @param params 查询参数
   */
  protected async getData(path: string, params?: Record<string, any>): Promise<T> {
    try {
      // 添加通用参数
      const enhancedParams = this.addCommonParams(params);

      // 根据环境变量决定使用Mock数据还是真实API
      if (this.useMockData) {
        console.log(`[Mock] Getting data from mock for ${path}`);
        return await this.getMockData(enhancedParams);
      }

      // 使用真实API
      console.log(`[API] Getting data from API for ${path}`);
      const response = await ApiService.get(this.getApiPath(path), enhancedParams);
      return this.handleResponse(response);
    } catch (error) {
      const apiError = error as ApiError;
      
      // 如果是认证错误，尝试使用模拟数据
      if (apiError.type === ApiErrorType.AUTHENTICATION) {
        console.warn('Authentication error, falling back to mock data');
        return this.getMockData(params);
      }
      
      throw error;
    }
  }

  /**
   * 提交数据
   * @param path API路径
   * @param data 请求数据
   * @param params 查询参数
   */
  protected async postData(path: string, data: T, params?: Record<string, any>): Promise<any> {
    try {
      // 添加通用参数
      const enhancedParams = this.addCommonParams(params);

      // 准备请求数据
      const requestData = this.prepareRequestData(data);

      // 如果使用Mock数据，则模拟成功响应
      if (this.useMockData) {
        console.log(`[Mock] Posting data to mock for ${path}`, data);
        // 模拟延迟
        await new Promise(resolve => setTimeout(resolve, 300));
        return { success: true, data: { id: Date.now() } };
      }

      // 使用真实API
      console.log(`[API] Posting data to API for ${path}`, requestData);
      const response = await ApiService.post(this.getApiPath(path), requestData, { params: enhancedParams });
      return response;
    } catch (error) {
      const apiError = error as ApiError;
      
      // 🔧 检查是否允许在认证错误时回退到Mock数据
      if (apiError.type === ApiErrorType.AUTHENTICATION) {
        const allowMockFallback = import.meta.env.VITE_ALLOW_MOCK_FALLBACK !== 'false';
        
        if (allowMockFallback) {
          console.warn('Authentication error, falling back to mock data');
          return this.getMockData(params);
        } else {
          console.error('Authentication error, mock fallback disabled - throwing error');
          throw new Error('Authentication required. Please login to create orders.');
        }
      }
      
      throw error;
    }
  }

  /**
   * 更新数据
   * @param path API路径
   * @param data 请求数据
   * @param params 查询参数
   */
  protected async putData(path: string, data: T, params?: Record<string, any>): Promise<any> {
    try {
      // 添加通用参数
      const enhancedParams = this.addCommonParams(params);

      // 准备请求数据
      const requestData = this.prepareRequestData(data);

      // 如果使用Mock数据，则模拟成功响应
      if (this.useMockData) {
        console.log(`[Mock] Putting data to mock for ${path}`, data);
        // 模拟延迟
        await new Promise(resolve => setTimeout(resolve, 300));
        return { success: true };
      }

      // 使用真实API
      console.log(`[API] Putting data to API for ${path}`, requestData);
      const response = await ApiService.put(this.getApiPath(path), requestData, { params: enhancedParams });
      return response;
    } catch (error) {
      const apiError = error as ApiError;
      
      // 如果是认证错误，尝试使用模拟数据
      if (apiError.type === ApiErrorType.AUTHENTICATION) {
        console.warn('Authentication error, falling back to mock data');
        return this.getMockData(params);
      }
      
      throw error;
    }
  }

  /**
   * 删除数据
   * @param path API路径
   * @param params 查询参数
   */
  protected async deleteData(path: string, params?: Record<string, any>): Promise<any> {
    try {
      // 添加通用参数
      const enhancedParams = this.addCommonParams(params);

      // 如果使用Mock数据，则模拟成功响应
      if (this.useMockData) {
        console.log(`[Mock] Deleting data from mock for ${path}`);
        // 模拟延迟
        await new Promise(resolve => setTimeout(resolve, 300));
        return { success: true };
      }

      // 使用真实API
      console.log(`[API] Deleting data from API for ${path}`);
      const response = await ApiService.delete(this.getApiPath(path), { params: enhancedParams });
      return response;
    } catch (error) {
      const apiError = error as ApiError;
      
      // 如果是认证错误，尝试使用模拟数据
      if (apiError.type === ApiErrorType.AUTHENTICATION) {
        console.warn('Authentication error, falling back to mock data');
        return this.getMockData(params);
      }
      
      throw error;
    }
  }
}

/**
 * 默认适配器
 * 当没有提供适配器时使用
 */
class DefaultAdapter<T, R = any> extends BaseApiAdapter<T, R> {
  fromApiResponse(response: ApiResponse<any>): T {
    // 递归处理可能存在的编码问题
    const processEncodingIssues = (data: any): any => {
      if (data === null || data === undefined) {
        return data;
      }

      if (Array.isArray(data)) {
        return data.map(item => processEncodingIssues(item));
      }

      if (typeof data === 'object') {
        const result: Record<string, any> = {};
        for (const key in data) {
          if (Object.prototype.hasOwnProperty.call(data, key)) {
            result[key] = processEncodingIssues(data[key]);
          }
        }
        return result;
      }

      // 修复字符串中的中文编码问题
      if (typeof data === 'string') {
        // 针对API返回的特殊编码格式处理
        try {
          // 检测API返回的Unicode转义序列格式 (\u00e6\u00b0 开头)
          if (/\\u00[a-f0-9]{2}/.test(data) || data.includes('\\u') || /[\u00e0-\u00ff]{2,}/.test(data)) {
            try {
              // 尝试解码此类特殊格式
              const decodedText = decodeURIComponent(escape(data));
              if (decodedText !== data) {
                console.log(`成功解码: "${data}" => "${decodedText}"`);
                return decodedText;
              }
            } catch (e) {
              console.warn('Failed to decode using escape/decodeURIComponent:', data);
            }
          }

          // 方法2: 常见中文字符替换表（针对特定模式的硬编码修复）
          const replacements: Record<string, string> = {
            'æ°"': '气',
            'åž«': '垫',
            'æœº': '机',
            'çº¸': '纸',
            'èƒ¶': '胶',
            'å¸¦': '带',
            'æŸ±': '柱',
            'è¢‹': '袋',
            'é«˜': '高',
            'è´¨': '质',
            'è®¾': '设',
            'å¤‡': '备',
            'å°': '封',
            'å¯': '可',
            'äº': '产',
            'å"': '品'
          };
          
          let result = data;
          for (const [pattern, replacement] of Object.entries(replacements)) {
            result = result.replace(new RegExp(pattern, 'g'), replacement);
          }
          
          // 如果替换发生了变化，返回结果
          if (result !== data) {
            console.log(`使用替换表解码: "${data}" => "${result}"`);
            return result;
          }

          // 方法3: 检查API返回的Unicode序列 (\u00e6)，尝试另一种方式解码
          if (data.includes('\\u')) {
            try {
              // 使用JSON.parse解析Unicode转义
              return JSON.parse(`"${data}"`);
            } catch (error) {
              console.warn('Failed to parse Unicode using JSON.parse:', data);
            }
          }
        } catch (e) {
          console.warn('编码转换失败:', e);
        }
      }

      return data;
    };

    // 处理响应数据
    let result: any;
    
    // 如果响应有data字段，则返回data
    if (response && response.data !== undefined) {
      result = processEncodingIssues(response.data);
    } else {
      // 否则返回整个响应
      result = processEncodingIssues(response);
    }
    
    return result as unknown as T;
  }
} 